/**
 * Shared API Client
 *
 * Single configured Axios instance for all backend API calls.
 * All services (engagement, player, atom, etc.) use this client
 * rather than creating their own Axios instances.
 *
 * Configuration:
 * - Base URL from `__API_BASE_URL__` Vite environment variable
 * - MS-CV correlation vector header (for distributed tracing)
 * - Auth interceptor placeholder (MSAL token injection)
 *
 * @module http
 *
 * @example
 * ```typescript
 * import { apiClient } from '@/shared/http';
 *
 * const response = await apiClient.get<MyType>('/api/v1/resource');
 * ```
 */
import axios from 'axios';
import { CorrelationVector } from 'mscv';
import { nextRequestCv, getLaunchCorrelationId } from './correlation-context';
import { trackException } from '@/shared/telemetry';
import { authClient } from '@/ShellWithStore';

/**
 * Shared Axios instance configured with the backend base URL.
 * All API modules import this instance instead of creating their own.
 */
export const apiClient = axios.create({
    baseURL: import.meta.env.__MOCK_MODE__ === 'true'
        ? import.meta.env.__MOCK_SERVER_URL__
        : (import.meta.env.__API_BASE_URL__ ?? ''),
    headers: {
        'Content-Type': 'application/json',
    },
});

/**
 * Request interceptor: stamps standard headers via getRequestHeaders().
 * - MS-CV increments per request (distributed tracing).
 * - X-Correlation-ID is the launch-scoped GUID (same for all calls in one launch).
 */
if (apiClient.interceptors) {
    apiClient.interceptors.request.use(async (config) => {
        const headers = await getRequestHeaders();
        for (const [key, value] of Object.entries(headers)) {
            if (!config.headers[key]) {
                config.headers[key] = value;
            }
        }
        return config;
    });

    apiClient.interceptors.response.use(
        undefined,
        (error: unknown) => {
            if (axios.isAxiosError(error)) {
                const urlPath = error.config?.url ?? 'unknown';
                const status = String(error.response?.status ?? 'network');
                const method = (error.config?.method ?? 'unknown').toUpperCase();
                trackException(
                    new Error(`Api.CallFailed: ${method} ${urlPath} → ${status}`),
                    { status, method, urlPath },
                );
            }
            return Promise.reject(error);
        },
    );
}


/**
 * Build the standard request headers that the Axios interceptor normally injects.
 * Used by `fetch` + `keepalive` calls that bypass Axios (e.g., visibilitychange handler).
 *
 * Acquires a Bearer token via MSAL (skipped when mock API is active) so both
 * the Axios interceptor and the fetch + keepalive path share the same auth header.
 */
export async function getRequestHeaders(): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'MS-CV': nextRequestCv() ?? CorrelationVector.createCorrelationVector(1).value,
    };
    const correlationId = getLaunchCorrelationId();
    if (correlationId) headers['X-Correlation-ID'] = correlationId;

    const apiScope = import.meta.env.__API_SCOPE__;
    if (apiScope) {
        try {
            const token = await authClient.acquireToken(apiScope);
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }
        } catch {
            console.warn('[api-client] Token acquisition failed — request will proceed without auth.');
        }
    }

    return headers;
}
